<template>
  <div class="container">
    <div class="row">
      <div class="form-group col-md-12">
        <span class="pregunta-conformidad">{{ intl.i18n.t('datos_factura.verificar_modal.pregunta') }}</span>
      </div>
    </div>
    <DxRadioGroup
      v-model="conforme"
      :items="opcionesConformidad"
      value-expr="value"
      display-expr="text"
      layout="vertical"
    />
    <div v-if="conforme === false" class="mt-3">
      <aside>
        {{ intl.i18n.t('datos_factura.verificar_modal.mensaje_ticket') }}
      </aside>
      <label class="form-label mt-2" for="observaciones-revision">
        {{ intl.i18n.t('datos_factura.verificar_modal.observaciones_label') }}
        <span class="dx-field-item-required-mark">&nbsp;*</span>
      </label>
      <DxTextArea
        id="observaciones-revision"
        v-model="observaciones"
        :height="120"
        :placeholder="intl.i18n.t('datos_factura.verificar_modal.observaciones_placeholder')"
      >
        <DxValidator validation-group="verificacionFacturaObs">
          <DxCustomRule
            :validation-callback="validarObservacionesObligatorias"
            :message="intl.i18n.t('datos_factura.verificar_modal.validacion_observaciones')"
          />
        </DxValidator>
      </DxTextArea>
    </div>
    <div class="mt-3">
      <DocumentCard
        v-bind="documentCardProps"
        @updated:document="onDocumentoSeleccionado"
        @added:document="onDocumentoSeleccionado"
        ref="documentCardRef"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { getCurrentInstance, onMounted, ref, computed } from 'vue';
import DxRadioGroup from 'devextreme-vue/radio-group';
import DxTextArea from 'devextreme-vue/text-area';
import { DxCustomRule, DxValidator } from 'devextreme-vue/validator';
import validationEngine from 'devextreme/ui/validation_engine';
import { modalDefinedEmits, type ToolbarReference, useModalBuilder } from '@helpers/modal-building-helper';
import { messageBoxResult } from '@components/dialogs/message-box';
import { DxButton } from 'devextreme-vue/button';
import DocumentCard from '@components/document-card/DocumentCard.vue';
import type { IDocumentCardProps, IDocumentInitialState } from '@components/document-card/models/IDocumentCardProps';
import type { CardFile } from '@components/document-card/models/CardFile';
import { useIntl } from '@services/i18n';
import { toastError } from '@/.shared/services/notification-service';

const intl = useIntl();

export type VerificarFacturaModalResult = {
  result: typeof messageBoxResult.ok;
  conforme: boolean;
  observaciones: string;
  file?: File;
};

const conforme = ref<boolean | null>(null);
const observaciones = ref('');
const documentoASubir = ref<File>();
const documentCardRef = ref<InstanceType<typeof DocumentCard>>();

const opcionesConformidad = computed(() => [
  { value: true, text: intl.i18n.t('datos_factura.verificar_modal.opcion_conforme') },
  { value: false, text: intl.i18n.t('datos_factura.verificar_modal.opcion_no_conforme') },
]);

const documentCardProps = computed<IDocumentCardProps>(() => ({
  title: intl.i18n.t('datos_factura.form.factura_propia'),
  required: false,
  actions: ['open', 'download', 'add', 'replace'],
  maxFiles: 1,
  allowedFileFormats: ['all'],
  align: 'horizontal',
  initialDocumentList: [] as IDocumentInitialState[],
  multiple: false,
}));

onMounted(() => {
  const modal = useModalBuilder();
  modal.setContext(getCurrentInstance()!);
  configureButtons(modal);
});

function validarObservacionesObligatorias(e: { value?: string | null }) {
  return (e.value?.trim().length ?? 0) > 0;
}

function onDocumentoSeleccionado(file: CardFile) {
  documentoASubir.value = file?.file ?? undefined;
}

function configureButtons(modal: ReturnType<typeof useModalBuilder>) {
  let okButton: ToolbarReference<DxButton>;
  let cancelButton: ToolbarReference<DxButton>;
  ({ okButton, cancelButton } = modal.standardButtons.addOkCancel());
  okButton.update((w) => {
    w.onClick = async () => {
      if (conforme.value === null) {
        toastError(intl.i18n.t('datos_factura.verificar_modal.validacion_elegir_opcion'));
        return;
      }
      if (conforme.value === false) {
        const obsValid = validationEngine.validateGroup('verificacionFacturaObs');
        if (!obsValid.isValid) {
          return;
        }
      }
      const validationResult = documentCardRef.value?.isValid ?? false;
      if (!validationResult) {
        return;
      }
      const payload: VerificarFacturaModalResult = {
        result: messageBoxResult.ok,
        conforme: conforme.value === true,
        observaciones: observaciones.value?.trim() ?? '',
        file: documentoASubir.value,
      };
      modal.close(payload);
    };
  });
  cancelButton.update((w) => {
    w.onClick = () => modal.close({ result: messageBoxResult.cancel });
  });
}

defineEmits([...modalDefinedEmits]);
</script>